import { FC } from 'react'
import { format, isSameDay } from 'date-fns'
import { Duration } from 'luxon'

import {
  Header,
  Page,
  Side,
  SideProps,
  Stepper,
  Text,
  StepperStepSkeleton as UiKitStepperStepSkeleton,
  Widget,
} from '@revolut-internal/ui-kit'

import { useChannel } from 'hooks/useChannel'
import { useAgentAttendanceTimelineQuery } from 'modules/api/analytics/queries'
import { Agent } from 'types'
import { formatDuration } from 'utils/formatTimeAndDuration'

import { ShiftTimelineSegment } from './ShiftTimelineSegment'

type ShiftTimelineProps = Required<Pick<SideProps, 'open' | 'onClose'>> & {
  shiftId?: string
  agent?: Agent
}

const formatLastActivity = (timestamp?: number) => {
  if (!timestamp) {
    return '-'
  }

  const timeFromNow = formatDuration(Duration.fromMillis(Date.now() - timestamp))

  return `${format(timestamp, 'HH:mm')} (${timeFromNow} ago)`
}

const StepperStepSkeleton = UiKitStepperStepSkeleton as FC

export const ShiftTimelineSide = ({
  shiftId,
  agent,
  open,
  onClose,
}: ShiftTimelineProps) => {
  const { channel, isDante } = useChannel()

  const { data: timeline, isPending } = useAgentAttendanceTimelineQuery(
    { channel, shiftId: shiftId! },
    {
      enabled: open && Boolean(shiftId),
    },
  )
  const isInterday = Boolean(
    timeline?.timeline.segments[0].startDate &&
      timeline?.timeline.segments.at(-1)?.endDate &&
      !isSameDay(
        timeline?.timeline.segments[0].startDate,
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        timeline?.timeline.segments.at(-1)?.endDate!,
      ),
  )

  return (
    <Page.Side behaviour="cover" usePortal>
      <Side open={open} variant="wide" onClose={onClose}>
        <Header variant="modal">
          <Header.CloseButton aria-label="Close" />
          <Header.Title>{agent?.name ?? 'Shift timeline'}</Header.Title>
        </Header>

        <Widget pb="s-8">
          {isPending && (
            <Stepper>
              <StepperStepSkeleton />
              <StepperStepSkeleton />
              <StepperStepSkeleton />
            </Stepper>
          )}
          {timeline &&
            timeline.timeline.segments.map((segment, index) => (
              <ShiftTimelineSegment
                key={index}
                adherence={timeline.timeline.adherence}
                timelineSegment={segment}
                isFirst={index === 0}
                showDates={isInterday}
              />
            ))}
          <Text variant="emphasis2" pl="s-16" mt="s-4">
            {isDante
              ? `Last task assigned at ${formatLastActivity(
                  timeline?.lastAssignmentDate,
                )}`
              : `Last message sent at ${formatLastActivity(timeline?.lastChatMessage)}`}
          </Text>
        </Widget>
      </Side>
    </Page.Side>
  )
}
